import React, { useRef, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { ComponentManager } from '../../components/ComponentManager';
import { ComponentRenderer } from '../../components/ComponentRenderer';
import { InvisibleComponentsBar } from '../../components/InvisibleComponentsBar';
import { ThemeProvider } from '../../contexts/ThemeContext';
import { CustomColorsProvider } from '../../contexts/CustomColorsContext';
import { PageContextProvider } from '../../contexts/PageContext';
import { PoweredByBadge } from '../../components/PoweredByBadge';
import { subscriptionsApi } from '../../api/client';
import type { Site, Component, PageLayout } from '../../schemas/validation';

interface PageEditorContentProps {
  siteId: string;
  pageId: string;
  pageSlug: string;
  site: Site;
  components: Component[];
  pageLayout: PageLayout | null;
  themeColors: any;
  themeError: string | null;
  isAdmin: boolean;
  componentRefreshTrigger: number;
  onAddComponent: () => void;
  onAddComponentAt: (position: number) => void;
  onEditInvisibleComponent: (component: Component) => void;
  onDeleteInvisibleComponent: (componentId: string) => void;
  onAddInvisibleComponent: () => void;
  onClearThemeError: () => void;
  onComponentSaved?: () => void;
  onComponentsChanged?: (components: Component[]) => void;
  showRoaryBanner?: boolean;
  onDismissRoaryBanner?: () => void;
}

export const PageEditorContent: React.FC<PageEditorContentProps> = ({
  siteId,
  pageId,
  pageSlug,
  site,
  components,
  pageLayout,
  themeColors,
  themeError,
  isAdmin,
  componentRefreshTrigger,
  onAddComponent,
  onAddComponentAt,
  onEditInvisibleComponent,
  onDeleteInvisibleComponent,
  onAddInvisibleComponent,
  onClearThemeError,
  onComponentSaved,
  onComponentsChanged,
  showRoaryBanner = false,
  onDismissRoaryBanner,
}) => {
  // Check subscription to determine if branding should show
  const [showBranding, setShowBranding] = useState(false);

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const response = await subscriptionsApi.getCurrentSubscription();
        // API returns { subscription: {...} | null, usage: { plan: { name: '...' } } }
        const planName = response.subscription?.planName || response.usage?.plan?.name;
        // Show branding if no plan or on FREE plan
        setShowBranding(!planName || planName.toUpperCase() === 'FREE');
      } catch (error) {
        console.warn('Could not check subscription for branding:', error);
        setShowBranding(true); // Default to show branding on error
      }
    };
    checkSubscription();
  }, []);

  // Get layout components split by content-area
  const getLayoutParts = () => {
    if (!pageLayout?.components) {
      console.log('🐉 [PageEditorContent] No layout components found');
      return { headerComponents: [], footerComponents: [] };
    }

    const sortedLayoutComponents = [...pageLayout.components].sort(
      (a, b) => (a.orderPosition || 0) - (b.orderPosition || 0)
    );

    const contentAreaIndex = sortedLayoutComponents.findIndex(c => c.type === 'content-area');

    if (contentAreaIndex < 0) {
      // Fallback: If no content-area, use heuristics based on component types
      // Navbar/header components go at the top, footer components go at the bottom
      const headerTypes = ['navbar', 'simple-header'];
      const footerTypes = ['footer'];

      const headerComponents = sortedLayoutComponents.filter(c => headerTypes.includes(c.type));
      const footerComponents = sortedLayoutComponents.filter(c => footerTypes.includes(c.type));

      return { headerComponents, footerComponents };
    }

    return {
      headerComponents: sortedLayoutComponents.slice(0, contentAreaIndex),
      footerComponents: sortedLayoutComponents.slice(contentAreaIndex + 1),
    };
  };

  const { headerComponents, footerComponents } = getLayoutParts();

  // Ref for the content container to attach native event listener
  const contentRef = useRef<HTMLDivElement>(null);

  // Use native DOM event listener to intercept link clicks in the page editor
  // This fires before React's synthetic event system and React Router
  useEffect(() => {
    if (!contentRef.current) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      if (link) {
        const href = link.getAttribute('href');
        // Allow app-level routes to pass through (not site pages)
        const appRoutes = ['/pricing', '/login', '/register', '/dashboard', '/admin'];
        if (href && appRoutes.some(route => href === route || href.startsWith(route + '/'))) {
          return;
        }
        // Allow external links that open in a new tab
        if (link.getAttribute('target') === '_blank') {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    const container = contentRef.current;
    container.addEventListener('click', handleClick, { capture: true });

    return () => {
      container.removeEventListener('click', handleClick, { capture: true });
    };
  }, []);

  return (
    <>
      {showRoaryBanner && (
        <div className="bg-orange-50 border-b border-orange-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <p className="text-sm text-orange-900">
              Roary filled this in — swap photos and tweak copy.
            </p>
            <button
              type="button"
              onClick={onDismissRoaryBanner}
              className="text-orange-700 hover:text-orange-900"
              aria-label="Dismiss Roary banner"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Theme Error Banner */}
      {themeError && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm text-yellow-800">
                <strong>Theme Loading Warning:</strong> {themeError}
              </span>
            </div>
            <button onClick={onClearThemeError} className="text-yellow-600 hover:text-yellow-800">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Invisible Components Bar */}
      <InvisibleComponentsBar
        components={components}
        onEdit={onEditInvisibleComponent}
        onDelete={onDeleteInvisibleComponent}
        onAdd={onAddInvisibleComponent}
      />

      {/* Page Content */}
      <div ref={contentRef} className="relative">
        {/* Component Manager - Wrapped with ThemeProvider, CustomColorsProvider, and PageContextProvider for theme support and page context */}
        <ThemeProvider theme={site?.theme || 'standard'}>
          <CustomColorsProvider
            customColors={themeColors || {}}
            fonts={{
              headingFont: themeColors?.headingFont,
              bodyFont: themeColors?.fontFamily,
            }}
          >
            <div
              style={{
                fontFamily: themeColors?.fontFamily
                  ? `"${themeColors.fontFamily}", system-ui, sans-serif`
                  : 'inherit',
              }}
            >
              <PageContextProvider pageId={pageId} siteId={siteId} pageSlug={pageSlug}>
                {/* Top of page anchor for smooth scroll navigation */}
                <div id="top" />

                {/* Layout Header Components (before content-area) */}
                {pageLayout && headerComponents.length > 0 && (
                  <div className="layout-header-components relative">
                    <div className="absolute top-0 left-0 right-0 z-10 bg-purple-100 border-b border-purple-300 px-3 py-1 text-xs text-purple-700 font-medium">
                      Layout: {pageLayout.name} (Header)
                    </div>
                    <div className="pt-6 opacity-75 pointer-events-none">
                      {headerComponents.map(component => (
                        <ComponentRenderer key={component.id} component={component} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Page Components */}
                <ComponentManager
                  pageId={pageId}
                  siteId={siteId}
                  onAddComponent={onAddComponent}
                  onAddComponentAt={onAddComponentAt}
                  refreshTrigger={componentRefreshTrigger}
                  isAdmin={isAdmin}
                  onComponentSaved={onComponentSaved}
                  onComponentsChanged={onComponentsChanged}
                  layoutLinkHeaderComponents={headerComponents as Component[]}
                  layoutLinkFooterComponents={footerComponents as Component[]}
                  currentPageSlug={pageSlug}
                />

                {/* Layout Footer Components (after content-area) */}
                {pageLayout && footerComponents.length > 0 && (
                  <div className="layout-footer-components relative">
                    <div className="absolute top-0 left-0 right-0 z-10 bg-purple-100 border-b border-purple-300 px-3 py-1 text-xs text-purple-700 font-medium">
                      Layout: {pageLayout.name} (Footer)
                    </div>
                    <div className="pt-6 opacity-75 pointer-events-none">
                      {footerComponents.map(component => (
                        <ComponentRenderer key={component.id} component={component} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Powered by badge for FREE tier users */}
                <PoweredByBadge show={showBranding} isEditorMode={true} />
              </PageContextProvider>
            </div>
          </CustomColorsProvider>
        </ThemeProvider>
      </div>
    </>
  );
};
